import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { DIST_EPSILON } from "geom/core";
import { line2IsParallel } from "geom/line2";

export function coplanarWallFilter(
  subRoof: GeneratedRoof,
  side: RoofSide,
  otherSubRoof: GeneratedRoof,
  otherSide: RoofSide,
): boolean {
  const {
    roofVolume: { edges },
  } = subRoof;
  const {
    roofVolume: { edges: otherEdges },
  } = otherSubRoof;

  const edge = edges.find((x) => x.side === side);
  const otherEdge = otherEdges.find((x) => x.side === otherSide);

  return Math.abs((edge?.startHeight ?? 0) - (otherEdge?.startHeight ?? 0)) > DIST_EPSILON;
}

export function nonCoplanarWallFilter(
  subRoof: GeneratedRoof,
  side: RoofSide,
  otherSubRoof: GeneratedRoof,
  otherSide: RoofSide,
): boolean {
  return !coplanarWallFilter(subRoof, side, otherSubRoof, otherSide);
}

export function nonCoplanarRakeFilter(
  subRoof: GeneratedRoof,
  side: RoofSide,
  otherSubRoof: GeneratedRoof,
  otherSide: RoofSide,
): boolean {
  if (side !== RoofSide.LEFT && side !== RoofSide.RIGHT && otherSide !== RoofSide.START && otherSide !== RoofSide.END) {
    return false;
  }

  const { roofVolume, rails } = subRoof;
  const { roofVolume: otherRoofVolume, rails: otherRails } = otherSubRoof;
  const { rectangularOverhangs } = roofVolume;
  const { rectangularOverhangs: otherRectangularOverhangs } = otherRoofVolume;
  const { leftOverhang, rightOverhang } = rectangularOverhangs;
  const { startOverhang, endOverhang } = otherRectangularOverhangs;

  const eaveOverhang = side === RoofSide.LEFT ? leftOverhang : rightOverhang;
  const rakeOverhang = otherSide === RoofSide.START ? startOverhang : endOverhang;

  if (eaveOverhang - rakeOverhang < DIST_EPSILON) {
    return false;
  }

  const { leftRail, rightRail } = rails;
  const { startRail, endRail } = otherRails;

  const eaveRail = side === RoofSide.LEFT ? leftRail : rightRail;
  const rakeRail = otherSide === RoofSide.START ? startRail : endRail;

  if (
    !line2IsParallel(
      eaveRail[0],
      eaveRail[1],
      eaveRail[3],
      eaveRail[4],
      rakeRail[0],
      rakeRail[1],
      rakeRail[3],
      rakeRail[4],
    )
  ) {
    return false;
  }

  return !coplanarWallFilter(subRoof, side, otherSubRoof, otherSide);
}
